import CreatableSelect from "react-select/creatable";
import AsyncCreatableSelect from "react-select/async-creatable";
import { SelectEditor } from "./selectEditor";

// Custom React Select Editor
export class CreateableSelectEditor extends SelectEditor {
  constructor(hotInstance) {
    super(hotInstance);

    this.objectName = null;
    this.asyncOptions = false;
    this.loadOptionsTimeout = null;
  }

  async prepare(row, col, prop, td, originalValue, cellProperties) {
    super.prepare(row, col, prop, td, originalValue, cellProperties);

    this.objectName = cellProperties.objectName;
    this.asyncOptions = cellProperties.asyncOptions ?? false;

    if (this.asyncOptions && this.originalValue?.id) {
      this.options = [
        {
          value: this.originalValue.id,
          label: this.originalValue.name,
        },
      ];
    }
  }

  async getOptionsFromUrl(url) {
    this.isLoading = true;

    const request = await fetch(url);
    const data = await request.json();
    const options = data.map((option) => ({
      value: option.id,
      label: option.name,
    }));
    this.options = options;

    this.isLoading = false;
  }

  renderComponent() {
    const selectedOption =
      this.options.find((option) => option.value === this.originalValue?.id) ??
      this.originalOption();

    if (this.asyncOptions) {
      return (
        <AsyncCreatableSelect
          cacheOptions
          defaultOptions
          loadOptions={this.loadOptions}
          value={selectedOption}
          onChange={this.handleChange}
          autoFocus
          menuIsOpen
          styles={this.getComponentStyles()}
          onBlur={this.close.bind(this)}
          onMenuClose={this.close.bind(this)}
          isClearable={true}
          isLoading={this.isLoading}
          onCreateOption={this.handleCreateOption}
        />
      );
    }

    return (
      <CreatableSelect
        options={this.options}
        value={selectedOption}
        onChange={this.handleChange}
        autoFocus
        menuIsOpen
        styles={this.getComponentStyles()}
        onBlur={this.close.bind(this)}
        onMenuClose={this.close.bind(this)}
        isClearable={true}
        isLoading={this.isLoading}
        onCreateOption={this.handleCreateOption}
      />
    );
  }

  originalOption() {
    if (!this.originalValue?.id) return null;

    return {
      value: this.originalValue.id,
      label: this.originalValue.name,
    };
  }

  loadOptions = (inputValue, callback) => {
    clearTimeout(this.loadOptionsTimeout);

    this.loadOptionsTimeout = setTimeout(async () => {
      try {
        const url = new URL(this.url, window.location.origin);
        url.searchParams.set(
          "q[name_or_mid_or_iris_crm_lead_id_text_cont]",
          inputValue
        );

        const request = await fetch(url);
        const data = await request.json();
        const options = data.map((option) => ({
          value: option.id,
          label: option.name,
        }));

        this.options = options;
        callback(options);
      } catch (error) {
        console.error(error);
        callback([]);
      }
    }, 250);
  };

  handleCreateOption = async (inputValue) => {
    this.isLoading = true;

    try {
      const response = await fetch(this.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector('[name="csrf-token"]')?.content || "",
        },
        body: JSON.stringify({
          [this.objectName]: {
            name: inputValue,
          },
        }),
      });

      if (!response.ok) throw new Error(`Failed to create ${this.objectName}`);

      const data = await response.json();
      const newOption = { value: data.id, label: data.name };

      this.options.push(newOption);
      this.value = newOption;

      const { row, col } = this;
      if (this.hot) {
        this.hot.setDataAtCell(row, col, {
          id: this.value?.value ?? "",
          name: this.value?.label ?? "",
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  };

  handleChange = (selectedOption) => {
    // Set the value
    this.value = {
      value: selectedOption?.value ?? "",
      label: selectedOption?.label ?? "",
    };

    // Update the cell value directly
    const { row, col } = this;

    if (this.hot) {
      this.hot.setDataAtCell(row, col, {
        id: this.value?.value ?? "",
        name: this.value?.label ?? "",
      });
    }

    this.close();
  };
}
